import type {
  AlarmLogRequestParams,
  AuditLogRequestParams,
  InputsRequestParams,
  OutputsRequestParams,
  PaginatedRequestParams,
  BillingRequestParams,
  UsageReport2RequestParams,
  UsageReportRequestParams,
} from '../api/nm-types'
import type { Input, LocalBackupType, RegionSortableField, User } from 'common/api/v1/types'

/** All domains must have an `all` property with the base key */
type QueryKeyDomain = {
  all: readonly [string]
}

const inputBase = 'inputs' as const
const input = {
  all: [inputBase] as const,
  get: (id: Input['id']) => [inputBase, id] as const,
  withLists: (params: InputsRequestParams) => [inputBase, 'withLists', params] as const,
}

const inputListBase = 'inputLists' as const
const inputList = {
  all: [inputListBase] as const,
  sendPlan: (inputListId: string, outputListId: string) =>
    [inputListBase, inputListId, 'sendPlan', outputListId] as const,
}

const outputBase = 'outputs' as const
const output = {
  all: [outputBase] as const,
  withLists: (params: OutputsRequestParams) => [outputBase, 'withLists', params] as const,
}

const alarmLogBase = 'alarmLogs' as const
const alarmLog = {
  all: [alarmLogBase] as const,
  list: (params: AlarmLogRequestParams) => [alarmLogBase, 'list', params] as const,
}

const auditLogBase = 'auditLogs' as const
const auditLog = {
  all: [auditLogBase] as const,
  list: (params: AuditLogRequestParams) => [auditLogBase, 'list', params] as const,
  descendants: (parentOperationId: string) => [auditLogBase, 'descendants', parentOperationId] as const,
}

const usageBase = 'usage' as const
const usage = {
  all: [usageBase] as const,
  report: (params: UsageReportRequestParams) => [usageBase, 'report', params] as const,
  report2: (params: UsageReport2RequestParams) => [usageBase, 'report2', params] as const,
}

const billingBase = 'billing' as const
const billing = {
  all: [billingBase] as const,
  report: (params: BillingRequestParams) => [billingBase, 'report', params] as const,
}

const regionBase = 'regions' as const
const region = {
  all: [regionBase] as const,
  lists: () => [regionBase, 'list'] as const,
  list: (params: PaginatedRequestParams<RegionSortableField>) => [regionBase, 'list', params] as const,
  details: () => [regionBase, 'detail'] as const,
  detail: (id: string) => [regionBase, 'detail', id] as const,
  scales: () => [regionBase, 'scale'] as const,
  scale: (id: string) => [regionBase, 'scale', id] as const,
}

const settingsBase = 'settings' as const
const settings = {
  all: [settingsBase] as const,
  /** Under the settings base so every settings save also refreshes the branding */
  branding: [settingsBase, 'branding'] as const,
}

const nmosBase = 'nmos' as const
const nmos = {
  all: [nmosBase] as const,
  scope: (user: Pick<User, 'group' | 'role'>) => [nmosBase, user.group, user.role] as const,
  senders: (search: string) => [nmosBase, 'senders', search] as const,
  receivers: (search: string) => [nmosBase, 'receivers', search] as const,
}

const backupBase = 'backups' as const
const backup = {
  all: [backupBase] as const,
  local: () => [backupBase, 'local'] as const,
  localByType: (type: LocalBackupType) => [backupBase, 'local', type] as const,
  externalS3: () => [backupBase, 'externalS3'] as const,
  externalS3PullStatus: () => [backupBase, 'externalS3', 'pullStatus'] as const,
  restoreStatus: () => [backupBase, 'restoreStatus'] as const,
}

const metricsBase = 'metrics' as const
/**
 * The parts of a metrics-series key that identify the stream. Grouped into one object so placeholder data can
 * carry over on a column/range change but never across streams, without indexing into the key tuple by position.
 */
export interface MetricsSeriesIdentity {
  scope: 'input' | 'output'
  scopeId: string
  table: string
  streamId?: string
  channelId?: string
  applianceId?: string
}
const metrics = {
  all: [metricsBase] as const,
  series: (identity: MetricsSeriesIdentity, columns: string, from: number, to: number) =>
    [metricsBase, 'series', identity, columns, from, to] as const,
}

const recoveryBase = 'recovery' as const
const recovery = {
  all: [recoveryBase] as const,
  config: () => [recoveryBase, 'config'] as const,
}

const aiAssistantBase = 'aiAssistant' as const
const aiAssistant = {
  all: [aiAssistantBase] as const,
  eula: (edgeUserId: string) => [aiAssistantBase, 'eula', edgeUserId] as const,
  threads: () => [aiAssistantBase, 'threads'] as const,
  thread: (id: string) => [aiAssistantBase, 'thread', id] as const,
  job: (id: string) => [aiAssistantBase, 'job', id] as const,
  currentThread: () => [aiAssistantBase, 'currentThread'] as const,
}

/**
 * Centralized query keys for TanStack Query.
 * Each domain's `all[0]` is the base key - must be unique across domains.
 */
export const keys = {
  alarmLog,
  auditLog,
  billing,
  input,
  inputList,
  output,
  usage,
  region,
  settings,
  nmos,
  backup,
  metrics,
  recovery,
  aiAssistant,
} satisfies Record<string, QueryKeyDomain>
